import React,{useCallback,useEffect,useMemo,useState}from"react";
import{
  Autocomplete,Box,Button,Dialog,DialogActions,DialogContent,
  DialogTitle,FormControl,InputLabel,MenuItem,Paper,Select,
  Table,TableBody,TableCell,TableContainer,TableHead,
  TablePagination,TableRow,TextField,Typography
}from"@mui/material";

const API_BASE="/api/purchases/nomina";

const MONTHS=[
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function today(){
  const date=new Date();
  return[
    date.getFullYear(),
    String(date.getMonth()+1).padStart(2,"0"),
    String(date.getDate()).padStart(2,"0")
  ].join("-");
}

function decimalValue(value){
  const number=Number(String(value??"0").replace(",","."));
  return Number.isFinite(number)?number:0;
}

function money(value){
  return decimalValue(value).toFixed(2);
}

function normalizeDecimal(value){
  let result=String(value??"")
    .replace(/,/g,".")
    .replace(/[^0-9.]/g,"");

  const index=result.indexOf(".");

  if(index!==-1){
    result=result.slice(0,index+1)+result.slice(index+1).replace(/\./g,"");
  }

  const parts=result.split(".");

  if(parts.length===2){
    result=`${parts[0]}.${parts[1].slice(0,2)}`;
  }

  return result;
}

function dateLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  return parts.length===3
    ?`${parts[2]}/${parts[1]}/${parts[0]}`
    :String(value);
}

function monthLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  const month=Number(parts[1]);

  return MONTHS[month-1]&&parts[0]
    ?`${MONTHS[month-1]} ${parts[0]}`
    :"-";
}

function timeLabel(value){
  return value?String(value).slice(0,5):"-";
}

function employeeName(employee){
  return employee?.razon_social||employee?.nombre_comercial||"";
}

function stateLabel(value){
  const labels={
    PENDIENTE:"Pendiente",
    APROBADA:"Aprobada",
    LIQUIDADA:"Liquidada",
    ANULADA:"Anulada"
  };

  return labels[value]||value||"-";
}

function initialForm(){
  return{
    empleado_id:"",
    fecha:today(),
    hora_inicio:"",
    hora_fin:"",
    descuento_almuerzo_minutos:"0",
    tarifa_hora:"",
    observacion:""
  };
}

function calculateOvertime(form){
  const invalid={
    valid:false,
    minutes:0,
    hours:0,
    total:0,
    crossesMidnight:false
  };

  if(!form.hora_inicio||!form.hora_fin)return invalid;

  const[startHour,startMinute]=form.hora_inicio.split(":").map(Number);
  const[endHour,endMinute]=form.hora_fin.split(":").map(Number);

  if(
    !Number.isInteger(startHour)||
    !Number.isInteger(startMinute)||
    !Number.isInteger(endHour)||
    !Number.isInteger(endMinute)||
    startHour<0||
    startHour>23||
    endHour<0||
    endHour>23||
    startMinute<0||
    startMinute>59||
    endMinute<0||
    endMinute>59
  ){
    return invalid;
  }

  const start=startHour*60+startMinute;
  let end=endHour*60+endMinute;
  const crossesMidnight=end<=start;

  if(crossesMidnight)end+=1440;

  const breakMinutes=Number(form.descuento_almuerzo_minutos||0);
  const worked=end-start-breakMinutes;

  if(
    !Number.isInteger(breakMinutes)||
    breakMinutes<0||
    worked<=0
  ){
    return{...invalid,crossesMidnight};
  }

  const hours=worked/60;
  const total=Math.round(
    (worked/60)*decimalValue(form.tarifa_hora)*100
  )/100;

  return{
    valid:true,
    minutes:worked,
    hours,
    total,
    crossesMidnight
  };
}

function MoneyInput({
  label,value,onChange,dashboardTheme,disabled=false
}){
  return(
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={event=>onChange(normalizeDecimal(event.target.value))}
      onBlur={()=>onChange(value===""?"":money(value))}
      inputProps={{inputMode:"decimal"}}
      sx={dashboardTheme.inputStyles}
    />
  );
}

export default function Overtime({
  empresaId,
  token,
  canView,
  canCreate,
  canEdit,
  canDelete,
  dashboardTheme,
  showError,
  showSuccess,
  showInfo,
  parseError,
  isMobile
}){
  const[employees,setEmployees]=useState([]);
  const[configurations,setConfigurations]=useState([]);
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionLoading,setActionLoading]=useState("");
  const[dialogOpen,setDialogOpen]=useState(false);
  const[editingItem,setEditingItem]=useState(null);
  const[annulItem,setAnnulItem]=useState(null);
  const[form,setForm]=useState(initialForm);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("TODOS");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);

  const rowsPerPage=isMobile?10:30;

  const employeeMap=useMemo(
    ()=>new Map(
      employees.map(employee=>[
        String(employee.id),
        employee
      ])
    ),
    [employees]
  );

  const selectedEmployee=useMemo(
    ()=>employeeMap.get(String(form.empleado_id))||null,
    [employeeMap,form.empleado_id]
  );

  const selectedConfiguration=useMemo(
    ()=>configurations.find(item=>
      String(item.empleado_id)===String(form.empleado_id)&&
      item.activo
    )||null,
    [configurations,form.empleado_id]
  );

  const preview=useMemo(
    ()=>calculateOvertime(form),
    [form]
  );

  const filteredItems=useMemo(()=>{
    const query=search.trim().toLowerCase();

    return items.filter(item=>{
      if(estado!=="TODOS"&&item.estado!==estado)return false;
      if(fechaDesde&&item.fecha<fechaDesde)return false;
      if(fechaHasta&&item.fecha>fechaHasta)return false;

      if(query){
        const employee=employeeMap.get(String(item.empleado_id));

        const searchable=[
          employeeName(employee),
          employee?.identificacion,
          employee?.cedula,
          employee?.ruc,
          item.fecha,
          monthLabel(item.fecha),
          item.observacion
        ].join(" ").toLowerCase();

        if(!searchable.includes(query))return false;
      }

      return true;
    });
  },[
    items,
    employeeMap,
    search,
    estado,
    fechaDesde,
    fechaHasta
  ]);

  const visibleItems=useMemo(()=>{
    const start=page*rowsPerPage;
    return filteredItems.slice(start,start+rowsPerPage);
  },[filteredItems,page,rowsPerPage]);

  const request=useCallback(async(url,options={})=>{
    const response=await fetch(url,{
      ...options,
      credentials:"include",
      headers:{
        ...(token?{Authorization:`Bearer ${token}`}:{ }),
        ...(options.body?{"Content-Type":"application/json"}:{ }),
        ...(options.headers||{})
      }
    });

    const contentType=response.headers.get("content-type")||"";

    const data=contentType.includes("application/json")
      ?await response.json()
      :{error:"El servidor devolvió una respuesta inválida."};

    if(!response.ok){
      const error=data?.error||data?.detail||data?.mensaje;

      const message=typeof error==="string"
        ?error
        :error&&typeof error==="object"
          ?Object.values(error).flat().join(" ")
          :parseError(data);

      throw new Error(message);
    }

    return data;
  },[token,parseError]);

  const loadData=useCallback(async()=>{
    if(!empresaId||!canView){
      setItems([]);
      setLoading(false);
      return;
    }

    try{
      setLoading(true);

      const params=new URLSearchParams({
        empresa_id:String(empresaId)
      });

      const[catalogs,overtime]=await Promise.all([
        request(`${API_BASE}/catalogos/?${params.toString()}`),
        request(`${API_BASE}/horas-extras/?${params.toString()}`)
      ]);

      setEmployees(
        Array.isArray(catalogs.empleados)
          ?catalogs.empleados
          :[]
      );

      setConfigurations(
        Array.isArray(catalogs.configuraciones)
          ?catalogs.configuraciones
          :[]
      );

      setItems(
        Array.isArray(overtime.results)
          ?overtime.results
          :[]
      );
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  },[empresaId,canView,request,showError]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  useEffect(()=>{
    setPage(0);
  },[search,estado,fechaDesde,fechaHasta,rowsPerPage]);

  useEffect(()=>{
    const lastPage=Math.max(
      0,
      Math.ceil(filteredItems.length/rowsPerPage)-1
    );

    if(page>lastPage)setPage(lastPage);
  },[filteredItems.length,rowsPerPage,page]);

  const formSet=(key,value)=>{
    setForm(previous=>({
      ...previous,
      [key]:value
    }));
  };

  const defaultRateForEmployee=employeeId=>{
    const config=configurations.find(item=>
      String(item.empleado_id)===String(employeeId)&&
      item.activo
    );

    return config?money(config.tarifa_hora_extra):"";
  };

  const handleEmployeeChange=employee=>{
    const empleadoId=employee?.id||"";

    setForm(previous=>({
      ...previous,
      empleado_id:empleadoId,
      tarifa_hora:defaultRateForEmployee(empleadoId)
    }));
  };

  const openCreate=()=>{
    setEditingItem(null);
    setForm(initialForm());
    setDialogOpen(true);
  };

  const openEdit=item=>{
    if(item.estado!=="PENDIENTE"){
      showInfo("Solo puedes editar horas extras pendientes.");
      return;
    }

    setEditingItem(item);

    setForm({
      empleado_id:item.empleado_id||"",
      fecha:item.fecha||today(),
      hora_inicio:item.hora_inicio
        ?timeLabel(item.hora_inicio)
        :"",
      hora_fin:item.hora_fin
        ?timeLabel(item.hora_fin)
        :"",
      descuento_almuerzo_minutos:String(
        item.descuento_almuerzo_minutos??0
      ),
      tarifa_hora:money(item.tarifa_hora),
      observacion:item.observacion||""
    });

    setDialogOpen(true);
  };

  const closeDialog=()=>{
    if(saving)return;
    setDialogOpen(false);
    setEditingItem(null);
    setForm(initialForm());
  };

  const validateForm=()=>{
    if(!form.empleado_id){
      showInfo("Selecciona un empleado.");
      return false;
    }

    if(!form.fecha){
      showInfo("Selecciona la fecha de las horas extras.");
      return false;
    }

    if(!form.hora_inicio||!form.hora_fin){
      showInfo("Ingresa la hora de inicio y finalización.");
      return false;
    }

    if(!preview.valid){
      showInfo("Revisa el horario y los minutos de descanso.");
      return false;
    }

    if(
      form.tarifa_hora===""||
      decimalValue(form.tarifa_hora)<=0
    ){
      showInfo("La tarifa por hora debe ser mayor a cero.");
      return false;
    }

    if(editingItem&&editingItem.estado!=="PENDIENTE"){
      showInfo("Solo puedes modificar registros pendientes.");
      return false;
    }

    return true;
  };

  const saveOvertime=async()=>{
    if(saving||!validateForm())return;

    try{
      setSaving(true);

      const isEditing=Boolean(editingItem);

      const payload={
        empresa_id:empresaId,
        fecha:form.fecha,
        hora_inicio:form.hora_inicio,
        hora_fin:form.hora_fin,
        descuento_almuerzo_minutos:Number(
          form.descuento_almuerzo_minutos||0
        ),
        tarifa_hora:money(form.tarifa_hora),
        observacion:form.observacion.trim()
      };

      if(!isEditing){
        payload.empleado_id=form.empleado_id;
      }

      const url=isEditing
        ?`${API_BASE}/horas-extras/${editingItem.id}/`
        :`${API_BASE}/horas-extras/`;

      const data=await request(url,{
        method:isEditing?"PATCH":"POST",
        body:JSON.stringify(payload)
      });

      showSuccess(
        data.mensaje||
        (isEditing
          ?"Horas extras actualizadas correctamente."
          :"Horas extras registradas correctamente.")
      );

      setDialogOpen(false);
      setEditingItem(null);
      setForm(initialForm());

      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const approveOvertime=async item=>{
    if(actionLoading)return;

    try{
      setActionLoading(String(item.id));

      const data=await request(
        `${API_BASE}/horas-extras/${item.id}/aprobar/`,
        {
          method:"POST",
          body:JSON.stringify({
            empresa_id:empresaId
          })
        }
      );

      showSuccess(
        data.mensaje||"Horas extras aprobadas correctamente."
      );

      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading("");
    }
  };

  const annulOvertime=async()=>{
    if(!annulItem||actionLoading)return;

    try{
      setActionLoading(String(annulItem.id));

      const data=await request(
        `${API_BASE}/horas-extras/${annulItem.id}/`,
        {
          method:"DELETE",
          body:JSON.stringify({
            empresa_id:empresaId
          })
        }
      );

      showSuccess(
        data.mensaje||"Horas extras anuladas correctamente."
      );

      setAnnulItem(null);
      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading("");
    }
  };

  const primaryButtonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:34,
    px:1.7,
    borderRadius:"9px",
    textTransform:"none",
    fontSize:12.5,
    fontWeight:600,
    boxShadow:"none"
  };

  const outlinedButtonSx={
    minHeight:32,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontSize:12,
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const dangerButtonSx={
    ...outlinedButtonSx,
    color:"#D32F2F",
    borderColor:"#D32F2F",
    "&:hover":{
      borderColor:"#D32F2F",
      bgcolor:"rgba(211,47,47,0.08)",
      boxShadow:"none"
    }
  };

  const inputStyles=dashboardTheme.inputStyles;
  const tableStyles=dashboardTheme.tableStyles;

  const mutedSx={
    fontSize:12.5,
    color:dashboardTheme.muted_text_color
  };

  const stateColor=value=>{
    if(value==="ANULADA")return"#D32F2F";
    if(value==="APROBADA")return"#16A34A";
    if(value==="LIQUIDADA")return dashboardTheme.primary_color;
    return"#D97706";
  };

  const menuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  const dialogPaperProps={
    elevation:0,
    sx:{
      bgcolor:dashboardTheme.card_background,
      color:dashboardTheme.text_color,
      border:`1px solid ${dashboardTheme.border_color}`,
      borderRadius:"12px",
      boxShadow:"none"
    }
  };

  return(
    <>
      <Box sx={{
        display:"flex",
        alignItems:{xs:"stretch",sm:"center"},
        justifyContent:"space-between",
        flexDirection:{xs:"column",sm:"row"},
        gap:1.5,
        mb:2
      }}>
        <Box>
          <Typography sx={{
            fontSize:{xs:24,sm:26,md:30},
            fontWeight:600,
            color:dashboardTheme.primary_color,
            lineHeight:1.2
          }}>
            Horas extras
          </Typography>

          <Typography sx={{...mutedSx,mt:.5}}>
            Registro y aprobación de horas extras por empleado
          </Typography>
        </Box>

        {canCreate&&(
          <Button
            variant="contained"
            onClick={openCreate}
            sx={primaryButtonSx}
          >
            Registrar horas extras
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{
        ...tableStyles.wrapper,
        boxShadow:"none"
      }}>
        <Box sx={{
          ...tableStyles.toolbar,
          display:"flex",
          flexWrap:"wrap",
          alignItems:"center",
          gap:1
        }}>
          <TextField
            size="small"
            label="Buscar empleado"
            value={search}
            onChange={event=>setSearch(event.target.value)}
            sx={{
              width:{xs:"100%",sm:280,md:350},
              ...inputStyles
            }}
          />

          <FormControl size="small" sx={{
            minWidth:{xs:"100%",sm:150},
            ...inputStyles
          }}>
            <InputLabel>Estado</InputLabel>

            <Select
              label="Estado"
              value={estado}
              onChange={event=>setEstado(event.target.value)}
              MenuProps={menuProps}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="APROBADA">Aprobada</MenuItem>
              <MenuItem value="LIQUIDADA">Liquidada</MenuItem>
              <MenuItem value="ANULADA">Anulada</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Desde"
            value={fechaDesde}
            onChange={event=>setFechaDesde(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...inputStyles
            }}
          />

          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={fechaHasta}
            onChange={event=>setFechaHasta(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...inputStyles
            }}
          />
        </Box>

        <Box sx={tableStyles.tableArea}>
          <TableContainer sx={tableStyles.container}>
            <Table size="small" sx={tableStyles.table}>
              <TableHead>
                <TableRow sx={tableStyles.headRow}>
                  <TableCell sx={{
                    ...tableStyles.headCell,
                    minWidth:190
                  }}>
                    Empleado
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Fecha
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Período
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Horario
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Descanso
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Horas
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Tarifa
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Total
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Estado
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleItems.map(item=>{
                  const employee=employeeMap.get(
                    String(item.empleado_id)
                  );

                  const pendiente=item.estado==="PENDIENTE";
                  const anulable=[
                    "PENDIENTE","APROBADA"
                  ].includes(item.estado);

                  return(
                    <TableRow
                      key={item.id}
                      hover
                      sx={tableStyles.bodyRow}
                    >
                      <TableCell>
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:600,
                          color:dashboardTheme.text_color
                        }}>
                          {employeeName(employee)||"Empleado"}
                        </Typography>

                        {(employee?.identificacion||employee?.cedula)&&(
                          <Typography sx={{
                            fontSize:11,
                            color:dashboardTheme.muted_text_color,
                            mt:.2
                          }}>
                            {employee.identificacion||employee.cedula}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {dateLabel(item.fecha)}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {monthLabel(item.fecha)}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {timeLabel(item.hora_inicio)}
                        {" - "}
                        {timeLabel(item.hora_fin)}
                      </TableCell>

                      <TableCell align="right">
                        {item.descuento_almuerzo_minutos} min
                      </TableCell>

                      <TableCell align="right">
                        {money(item.horas_calculadas)}
                      </TableCell>

                      <TableCell align="right">
                        ${money(item.tarifa_hora)}
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:700,
                          color:dashboardTheme.text_color
                        }}>
                          ${money(item.total)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{
                          fontSize:12.5,
                          fontWeight:600,
                          color:stateColor(item.estado)
                        }}>
                          {stateLabel(item.estado)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{
                          display:"flex",
                          alignItems:"center",
                          justifyContent:"flex-end",
                          flexWrap:"wrap",
                          gap:.7
                        }}>
                          {canEdit&&pendiente&&(
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={()=>openEdit(item)}
                              disabled={Boolean(actionLoading)}
                              sx={outlinedButtonSx}
                            >
                              Editar
                            </Button>
                          )}

                          {canEdit&&pendiente&&(
                            <Button
                              variant="contained"
                              size="small"
                              onClick={()=>approveOvertime(item)}
                              disabled={Boolean(actionLoading)}
                              sx={primaryButtonSx}
                            >
                              {actionLoading===String(item.id)
                                ?"Procesando..."
                                :"Aprobar"}
                            </Button>
                          )}

                          {canDelete&&anulable&&(
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={()=>setAnnulItem(item)}
                              disabled={Boolean(actionLoading)}
                              sx={dangerButtonSx}
                            >
                              Anular
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading&&filteredItems.length===0&&(
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={tableStyles.emptyCell}
                    >
                      {canView
                        ?"No hay registros de horas extras."
                        :"No tienes permisos para consultar horas extras."}
                    </TableCell>
                  </TableRow>
                )}

                {loading&&(
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={tableStyles.emptyCell}
                    >
                      Cargando horas extras...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_event,next)=>setPage(next)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({from,to,count})=>
            `${from}-${to} de ${count}`
          }
          sx={tableStyles.pagination}
        />
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          {editingItem
            ?"Editar horas extras"
            :"Registrar horas extras"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{
            display:"grid",
            gridTemplateColumns:{
              xs:"1fr",
              sm:"repeat(2,minmax(0,1fr))"
            },
            gap:1.5,
            pt:1
          }}>
            <Box sx={{
              gridColumn:{xs:"auto",sm:"span 2"}
            }}>
              <Autocomplete
                options={employees}
                value={selectedEmployee}
                disabled={Boolean(editingItem)}
                getOptionLabel={employee=>{
                  const identificacion=
                    employee.identificacion||
                    employee.cedula||
                    employee.ruc||
                    "";

                  return`${employeeName(employee)}${identificacion?` - ${identificacion}`:""}`;
                }}
                isOptionEqualToValue={(option,value)=>
                  String(option.id)===String(value.id)
                }
                onChange={(_event,value)=>
                  handleEmployeeChange(value)
                }
                filterOptions={(options,{inputValue})=>{
                  const query=inputValue.toLowerCase().trim();

                  if(!query)return options.slice(0,50);

                  return options.filter(item=>
                    employeeName(item).toLowerCase().includes(query)||
                    String(item.cedula||"").includes(query)||
                    String(item.ruc||"").includes(query)
                  ).slice(0,50);
                }}
                renderInput={params=>(
                  <TextField
                    {...params}
                    size="small"
                    label="Empleado"
                    placeholder="Buscar empleado..."
                    sx={inputStyles}
                  />
                )}
                noOptionsText="No se encontraron empleados"
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha de horas extras"
              value={form.fecha}
              onChange={event=>formSet(
                "fecha",
                event.target.value
              )}
              InputLabelProps={{shrink:true}}
              sx={inputStyles}
            />

            <MoneyInput
              label="Tarifa por hora"
              value={form.tarifa_hora}
              onChange={value=>formSet(
                "tarifa_hora",
                value
              )}
              dashboardTheme={dashboardTheme}
            />

            <TextField
              fullWidth
              size="small"
              type="time"
              label="Hora de inicio"
              value={form.hora_inicio}
              onChange={event=>formSet(
                "hora_inicio",
                event.target.value
              )}
              InputLabelProps={{shrink:true}}
              inputProps={{step:60}}
              sx={inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              type="time"
              label="Hora de finalización"
              value={form.hora_fin}
              onChange={event=>formSet(
                "hora_fin",
                event.target.value
              )}
              InputLabelProps={{shrink:true}}
              inputProps={{step:60}}
              sx={inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Descanso o almuerzo (minutos)"
              value={form.descuento_almuerzo_minutos}
              onChange={event=>formSet(
                "descuento_almuerzo_minutos",
                event.target.value.replace(/[^0-9]/g,"")
              )}
              inputProps={{min:0,step:1}}
              sx={inputStyles}
            />

            <Box sx={{
              display:"flex",
              alignItems:"center",
              gap:1.5,
              p:1.2,
              border:`1px solid ${dashboardTheme.border_color}`,
              borderRadius:"9px",
              bgcolor:dashboardTheme.dashboard_background
            }}>
              <Box>
                <Typography sx={mutedSx}>
                  Horas efectivas
                </Typography>

                <Typography sx={{
                  fontSize:19,
                  fontWeight:700,
                  color:dashboardTheme.text_color
                }}>
                  {preview.valid
                    ?money(preview.hours)
                    :"0.00"} h
                </Typography>
              </Box>

              <Box sx={{
                ml:"auto",
                textAlign:"right"
              }}>
                <Typography sx={mutedSx}>
                  Total
                </Typography>

                <Typography sx={{
                  fontSize:19,
                  fontWeight:700,
                  color:dashboardTheme.primary_color
                }}>
                  ${preview.valid
                    ?money(preview.total)
                    :"0.00"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              gridColumn:{xs:"auto",sm:"span 2"}
            }}>
              <TextField
                fullWidth
                size="small"
                label="Observación (opcional)"
                value={form.observacion}
                onChange={event=>formSet(
                  "observacion",
                  event.target.value
                )}
                sx={inputStyles}
              />
            </Box>

            {preview.valid&&preview.crossesMidnight&&(
              <Typography sx={{
                ...mutedSx,
                gridColumn:{xs:"auto",sm:"span 2"}
              }}>
                La hora de finalización corresponde al día siguiente.
              </Typography>
            )}

            {selectedConfiguration&&(
              <Typography sx={{
                ...mutedSx,
                gridColumn:{xs:"auto",sm:"span 2"}
              }}>
                Tarifa configurada: ${money(
                  selectedConfiguration.tarifa_hora_extra
                )} por hora.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p:2,
          pt:1,
          gap:.7
        }}>
          <Button
            variant="outlined"
            onClick={closeDialog}
            disabled={saving}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveOvertime}
            disabled={
              saving||
              (!editingItem&&!canCreate)||
              (Boolean(editingItem)&&!canEdit)
            }
            sx={primaryButtonSx}
          >
            {saving
              ?"Guardando..."
              :editingItem
                ?"Guardar cambios"
                :"Registrar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(annulItem)}
        onClose={()=>!actionLoading&&setAnnulItem(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Anular horas extras
        </DialogTitle>

        <DialogContent>
          {annulItem&&(
            <Typography sx={{
              fontSize:13.5,
              fontWeight:600,
              color:dashboardTheme.text_color
            }}>
              {dateLabel(annulItem.fecha)}
              {" · "}
              {money(annulItem.horas_calculadas)} horas
              {" · "}
              ${money(annulItem.total)}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{
          p:2,
          pt:1,
          gap:.7
        }}>
          <Button
            variant="outlined"
            onClick={()=>setAnnulItem(null)}
            disabled={Boolean(actionLoading)}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={annulOvertime}
            disabled={Boolean(actionLoading)}
            sx={{
              ...primaryButtonSx,
              bgcolor:"#D32F2F",
              "&:hover":{
                bgcolor:"#B71C1C",
                boxShadow:"none"
              }
            }}
          >
            {actionLoading?"Anulando...":"Anular"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}